import { CONTENT_PAGE_LABELS, type ContentPage } from '@idpe/schema';
import { Link, createFileRoute } from '@tanstack/react-router';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { Spinner } from '~/components/ui/Spinner.js';
import { useContentPagesList } from '~/features/contentPages/hooks.js';

export const Route = createFileRoute('/_auth/pages/')({
  component: PagesListPage,
});

function PagesListPage() {
  const { data, isLoading, error, refetch } = useContentPagesList();

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ O site</p>
        <h1 className="page-title">Páginas</h1>
        <p className="page-sub">
          As páginas institucionais do site. Cada uma é montada com blocos — trechos de texto,
          listas, chamadas — que você pode reordenar, esconder ou reescrever a qualquer momento.
        </p>
      </header>

      {isLoading && (
        <div className="dt__state">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <div className="dt__state">
          <p className="dt__state-title">Não foi possível carregar.</p>
          <p className="dt__state-sub">{error.displayMessage}</p>
          <Button variant="secondary" onClick={() => void refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {data && (
        <section className="pages-grid">
          {(Object.keys(CONTENT_PAGE_LABELS) as Array<keyof typeof CONTENT_PAGE_LABELS>).map(
            (slug) => {
              const page = data.find((p) => p.slug === slug) as ContentPage | undefined;
              return (
                <PageCard key={slug} slug={slug} label={CONTENT_PAGE_LABELS[slug]} page={page} />
              );
            },
          )}
        </section>
      )}
    </div>
  );
}

function PageCard({
  slug,
  label,
  page,
}: {
  slug: keyof typeof CONTENT_PAGE_LABELS;
  label: string;
  page: ContentPage | undefined;
}) {
  return (
    <article className="page-card">
      <header className="page-card__head">
        <h2 className="page-card__title">{label}</h2>
        {!page && <Badge tone="muted">não carregada</Badge>}
        {page && (
          <Badge
            tone={
              page.status === 'publicado'
                ? 'success'
                : page.status === 'arquivado'
                  ? 'muted'
                  : 'neutral'
            }
          >
            {page.status}
          </Badge>
        )}
      </header>
      {page && (
        <p className="page-card__meta">
          {page.blocos.length} {page.blocos.length === 1 ? 'bloco' : 'blocos'}
          {page.publicadoEm && (
            <span style={{ color: 'var(--fg-muted)' }}>
              {' · '}publicada em {new Date(page.publicadoEm).toLocaleDateString('pt-BR')}
            </span>
          )}
        </p>
      )}
      <footer className="page-card__actions">
        <Link to="/pages/$slug" params={{ slug }}>
          <Button variant="secondary" size="sm" fullWidth>
            Editar blocos
          </Button>
        </Link>
      </footer>
    </article>
  );
}
